import {
  ATIVO_CAIXA_SUBVIEWS,
  ATIVO_PORTFOLIO_SUBVIEWS,
  ATIVO_PRIMARY_TABS,
  type AtivoCaixaSubview,
  type AtivoPortfolioSubview,
  type AtivoPrimaryTab,
  labelForAtivoPortfolioSubview,
  labelForAtivoTab,
} from '@/libs/ativo-route';

export type FiiScreenToolInput = {
  screenId: 'fii-tab';
  params: {
    ticker: string;
    tab: AtivoPrimaryTab;
    subview?: AtivoCaixaSubview | AtivoPortfolioSubview;
  };
};

export type FixedIncomeScreenToolInput = {
  screenId: 'fixed-income';
  params: {
    view: 'catalog' | 'compare' | 'simulate';
  };
};

export type CurrentScreenToolInput = FiiScreenToolInput | FixedIncomeScreenToolInput;

export type CurrentScreenDescriptor = {
  id: CurrentScreenToolInput['screenId'];
  title: string;
  label: string;
  purpose: string;
  sections: string[];
  suggestedQuestions: string[];
  toolInput: CurrentScreenToolInput;
};

const tabDetails: Record<AtivoPrimaryTab, Omit<CurrentScreenDescriptor, 'id' | 'title' | 'label' | 'toolInput'>> = {
  resumo: {
    purpose: 'visão geral do ativo, com cotação, fundamentos, risco, dividendos, caixa e composição quando disponíveis',
    sections: ['cotação e indicadores', 'dividendos e patrimônio', 'risco e sensibilidade', 'composição da carteira'],
    suggestedQuestions: [
      'O que significam estes indicadores?',
      'Como interpretar o risco deste ativo?',
      'Quais dados merecem atenção?',
    ],
  },
  simulacao: {
    purpose: 'simulação de evolução do investimento com cenários de preço e reinvestimento de proventos',
    sections: ['premissas da simulação', 'evolução projetada', 'comparação com reinvestimento'],
    suggestedQuestions: [
      'O que esta simulação está assumindo?',
      'Como o reinvestimento muda o resultado?',
      'Qual é a diferença entre projeção e dado realizado?',
    ],
  },
  cotacao: {
    purpose: 'cotação e histórico de preços do ativo ao longo do período selecionado',
    sections: ['preço atual', 'variação e histórico', 'gráfico de cotação'],
    suggestedQuestions: [
      'Como ler este gráfico?',
      'O que significa a variação exibida?',
      'Qual é a diferença entre preço e retorno?',
    ],
  },
  liquidez: {
    purpose: 'indicadores de liquidez e negociação do ativo',
    sections: ['liquidez diária', 'volume negociado', 'histórico de negociação'],
    suggestedQuestions: ['Como interpretar a liquidez?', 'Por que liquidez importa?', 'O que este volume representa?'],
  },
  'dados-cvm': {
    purpose: 'dados cadastrais e informações regulatórias disponíveis na CVM',
    sections: ['identificação do fundo', 'patrimônio e cotas', 'dados cadastrais'],
    suggestedQuestions: [
      'O que significa este campo da CVM?',
      'Qual é a fonte deste dado?',
      'Como interpretar o patrimônio líquido?',
    ],
  },
  dividendos: {
    purpose: 'histórico de dividendos, rendimentos e amortizações do ativo',
    sections: ['histórico de pagamentos', 'rendimento por cota', 'gráficos e períodos'],
    suggestedQuestions: [
      'Como ler este histórico?',
      'O que é rendimento recorrente?',
      'Qual período está selecionado?',
    ],
  },
  patrimonio: {
    purpose: 'evolução do patrimônio líquido e dos principais componentes patrimoniais',
    sections: ['série histórica', 'patrimônio líquido', 'variações no período'],
    suggestedQuestions: [
      'O que explica esta evolução?',
      'Como interpretar o patrimônio líquido?',
      'Qual é a data de referência?',
    ],
  },
  caixa: {
    purpose: 'posição de caixa, valores a receber, obrigações e liquidez financeira do ativo',
    sections: ['visão geral de caixa', 'valores a receber', 'valores a pagar', 'carteira financeira'],
    suggestedQuestions: [
      'Como interpretar o caixa líquido?',
      'O que entra em valores a receber?',
      'O que significa esta cobertura de caixa?',
    ],
  },
  carteira: {
    purpose: 'composição da carteira do fundo e sua exposição a imóveis e ativos financeiros',
    sections: ['visão geral da carteira', 'imóveis e composição', 'riscos e contratos', 'ativos financeiros'],
    suggestedQuestions: [
      'Como interpretar a composição?',
      'Qual é a diferença entre imóveis e ativos financeiros?',
      'O que significa esta concentração?',
    ],
  },
  relatorios: {
    purpose: 'relatórios gerenciais e documentos recentes publicados para o ativo',
    sections: ['relatórios recentes', 'resumos gerenciais', 'documentos originais'],
    suggestedQuestions: [
      'O que este relatório destaca?',
      'Como comparar os relatórios?',
      'Onde está o documento original?',
    ],
  },
};

const caixaSubviewLabels: Record<AtivoCaixaSubview, string> = {
  'visao-geral': 'Visão geral',
  'detalhe-caixa': 'Detalhe de caixa',
  recebiveis: 'Recebíveis',
  'titulos-fundos': 'Títulos e fundos',
  'a-pagar': 'Obrigações',
};

const isPrimaryTab = (value: string): value is AtivoPrimaryTab =>
  (ATIVO_PRIMARY_TABS as readonly string[]).includes(value);
const isCaixaSubview = (value: string): value is AtivoCaixaSubview =>
  (ATIVO_CAIXA_SUBVIEWS as readonly string[]).includes(value);
const isPortfolioSubview = (value: string): value is AtivoPortfolioSubview =>
  (ATIVO_PORTFOLIO_SUBVIEWS as readonly string[]).includes(value);

export function getCurrentFiiScreenDescriptor({
  ticker,
  tab: rawTab,
  subview: rawSubview,
}: {
  ticker: string;
  tab: string;
  subview?: string;
}): CurrentScreenDescriptor | null {
  const tab = isPrimaryTab(rawTab) ? rawTab : null;
  if (!tab) return null;

  const subview =
    tab === 'caixa' && rawSubview && isCaixaSubview(rawSubview)
      ? rawSubview
      : tab === 'carteira' && rawSubview && isPortfolioSubview(rawSubview)
        ? rawSubview
        : undefined;
  const tabLabel = labelForAtivoTab(tab);
  const subviewLabel = subview
    ? tab === 'caixa'
      ? subview === 'visao-geral'
        ? null
        : caixaSubviewLabels[subview as AtivoCaixaSubview]
      : labelForAtivoPortfolioSubview(subview as AtivoPortfolioSubview)
    : null;
  const detail = tabDetails[tab];
  const label = subviewLabel ? `${tabLabel} · ${subviewLabel}` : tabLabel;
  const title = `${ticker} · ${label}`;

  return {
    id: 'fii-tab',
    title,
    label,
    ...detail,
    toolInput: {
      screenId: 'fii-tab',
      params: {
        ticker,
        tab,
        ...(subview ? { subview } : {}),
      },
    },
  };
}

const fixedIncomeViewDetails = {
  catalog: {
    title: 'Tesouro Direto · Catálogo',
    label: 'Catálogo de títulos',
    purpose: 'pesquisa e filtragem de títulos por indexador, vencimento, liquidez, taxa, preço e risco',
    sections: ['filtros de títulos', 'cotações e taxas', 'vencimentos e liquidez', 'métricas de risco'],
    suggestedQuestions: [
      'Como interpretar a taxa e o preço deste título?',
      'O que significa este indexador?',
      'Quais riscos e prazos devo observar?',
    ],
  },
  compare: {
    title: 'Tesouro Direto · Comparação',
    label: 'Comparação de títulos',
    purpose: 'comparação de títulos selecionados por taxa, preço, vencimento, fluxo de caixa e risco',
    sections: ['títulos selecionados', 'taxas e preços', 'fluxos de caixa', 'sensibilidade e risco'],
    suggestedQuestions: [
      'Quais são as diferenças entre estes títulos?',
      'Como o vencimento afeta a comparação?',
      'O que significa a sensibilidade à taxa de juros?',
    ],
  },
  simulate: {
    title: 'Tesouro Direto · Simulação de marcação a mercado',
    label: 'Simulação de marcação a mercado',
    purpose:
      'simulação de compra de um título em data passada ou presente com projeção de marcação a mercado sob cenário de juros',
    sections: [
      'título e data de compra',
      'cenário de juros e inflação',
      'preço projetado',
      'impostos, custos e retorno líquido',
    ],
    suggestedQuestions: [
      'O que esta simulação está assumindo?',
      'Como a taxa do cenário muda o preço do título?',
      'Como o imposto incide se eu vender antes do vencimento?',
    ],
  },
};

export function getCurrentFixedIncomeScreenDescriptor({
  view,
}: {
  view: 'catalog' | 'compare' | 'simulate';
}): CurrentScreenDescriptor {
  const detail = fixedIncomeViewDetails[view];
  return {
    id: 'fixed-income',
    ...detail,
    toolInput: {
      screenId: 'fixed-income',
      params: { view },
    },
  };
}
